// Работен лист „Нова инвентаризация / Редакция" — цяла страница (routes
// /inventory/stocktake/new и /inventory/stocktake/:stocktakeId/edit, вътре в
// Inventory layout-а). Оркестраторът: състоянието на документа, черновата
// (браузър + сървър, със заключване — `useStocktakeDraft` над общите
// `components/drafts/`; носи и приготвените рецепти), зареждането,
// клавиатурният поток, филтрите на изгледа, взаимозамените, „Нов продукт" и
// записът.
// „Започни" за склад от началния екран идва с router state `{warehouseId}`,
// изборът при незавършена — с `{draftChoice}`. След запис/отказ листът се
// връща на началния екран (`{openDoc}` → toast „Виж документа").
//
// Части в ./components: Header (полетата), SearchBar (търсачката, СКАЧА до
// реда; „Целият списък" и „Нов продукт" закачат в блока „За броене"),
// ProductsCard (статус · групи · „Скрий без наличност" + Table/LineCards с
// трите блока) с
// `useStocktakeRowsActions` (+ `buildStocktakeView`), SubstitutionsPanel +
// `useStocktakeSubstitutions`, Footer, DraftPrompt, `stocktakeDraft`,
// `useStocktakeLoad`, `useStocktakeLeave`, `useStocktakeSave` (пречките —
// `buildStocktakeIssues`). Клавиатурата е общата за модула
// (gridNavigation / useWorksheetSearch / useWorksheetKeyboard).
// Приготвените рецепти (`useStocktakeRecipes`) са над блоковете, в същата
// решетка (ключовете им са първи в стрелковия ред); съставките им се прибавят
// към намереното; изборът от търсачката и списъка — `useStocktakePicking`.
// „Занули склада" (само нова) — `useStocktakeZeroing`.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { App } from "antd";
import dayjs from "dayjs";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../hooks/useViewport";
import {
  useInterchangeGroups,
  useInventorySettings,
  useLastPrices,
  useProducts,
  useRecipes,
  useStockBalances,
  useWarehouses,
} from "../../../hooks/useInventory";
import { clampDecimals } from "../components/qty";
import { useStocktakeCountableProducts } from "./components/useStocktakeCountableProducts";
import { useGridNavigation, type GridRowKey } from "../components/gridNavigation";
import { useWorksheetKeyboard } from "../components/useWorksheetKeyboard";
import { useWorksheetSearch } from "../components/useWorksheetSearch";
import { InventoryTheme } from "../components/InventoryTheme";
import { DraftPresence } from "../components/drafts/DraftWorksheetParts";
import { StocktakeWorksheetHeader } from "./components/StocktakeWorksheetHeader";
import { StocktakeProductsCard } from "./components/StocktakeProductsCard";
import { StocktakeSearchBar } from "./components/StocktakeSearchBar";
import { StocktakeSubstitutionsPanel } from "./components/StocktakeSubstitutionsPanel";
import { StocktakeWorksheetFooter } from "./components/StocktakeWorksheetFooter";
import { StocktakeDraftPrompt } from "./components/StocktakeDraftPrompt";
import { useStocktakeRowsActions } from "./components/useStocktakeRowsActions";
import { useStocktakeSubstitutions } from "./components/useStocktakeSubstitutions";
import { useStocktakeLoad, type StocktakeRouterState } from "./components/useStocktakeLoad";
import { useStocktakeLeave } from "./components/useStocktakeLeave";
import { useStocktakeDraft } from "./components/useStocktakeDraft";
import { useStocktakeSave } from "./components/useStocktakeSave";
import { useStocktakeSearchRows } from "./components/useStocktakeSearchRows";
import { useStocktakeRecipes } from "./components/useStocktakeRecipes";
import { useStocktakePicking } from "./components/useStocktakePicking";
import { useStocktakeZeroing } from "./components/useStocktakeZeroing";
import type { StocktakeStatusFilter } from "./components/stocktakeView";
import { COL_COUNT, sheetOfStocktake } from "./components/stocktakeWorksheetShared";
import type { StocktakeDraft } from "./components/stocktakeDraft";

type Props = {
  warehouseId?: number | null;
  mode: "new" | "edit";
  // Регистрира пазач за незапазени промени в layout-а (Inventory.tsx), за да
  // могат sidebar стрелката/менюто да питат преди напускане. null при демонтаж.
  onRegisterLeaveGuard?: (guard: ((proceed: () => void) => void) | null) => void;
};

const HOME_PATH = "/inventory/stocktake";

export const StocktakeWorksheetPage = ({
  warehouseId = null,
  mode,
  onRegisterLeaveGuard,
}: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const { stocktakeId } = useParams();
  const { isDesktop, isNarrow } = useViewport();
  const routerState = (location.state as StocktakeRouterState) ?? null;

  const { data: warehouses = [] } = useWarehouses();
  const { data: products = [] } = useProducts();
  const { data: interchangeGroups = [] } = useInterchangeGroups();
  const { data: settings } = useInventorySettings();
  const vatRegistered = settings?.vatRegistered ?? true;

  // ─── Състояние на документа ───────────────────────────────────────────────
  const [date, setDate] = useState<string>(dayjs().format("YYYY-MM-DD"));
  // „Започни" за склад от началния екран бие склада по подразбиране
  const [whId, setWhId] = useState<number | null>(
    mode === "new" ? (routerState?.warehouseId ?? warehouseId) : warehouseId,
  );
  const [reason, setReason] = useState("");
  // Филтрите на изгледа (не се пазят в черновата — не са част от документа):
  // статус (чиповете Всички / За броене / Преброени / Непреброени / С
  // разлика), без наличност („Търсено" = 0) и група продукти
  const [statusFilter, setStatusFilter] = useState<StocktakeStatusFilter>("all");
  const [hideZeroStock, setHideZeroStock] = useState(false);
  const [groupFilter, setGroupFilter] = useState<string | null>(null);
  // Продукти, избрани от търсачката / новосъздадени / посочени от пречка:
  // остават видими при ВСЕКИ филтър, иначе скокът води до ред, който го няма.
  const [revealedProductIds, setRevealedProductIds] = useState<Set<number>>(new Set());
  // „Целият списък" и „Нов продукт" → блокът „За броене" (виж StocktakeSearchBar)
  const [pinnedProductIds, setPinnedProductIds] = useState<number[]>([]);
  // Запазеното „търсено" на редактирания запис (очакваното ПРЕДИ инвентаризацията)
  const [savedSystemByProduct, setSavedSystemByProduct] = useState<Map<number, number>>(
    new Map(),
  );
  // Взаимозамени: получател → донор. Наличието на ключ = двойката е приложена.
  const [appliedDonorByRecipient, setAppliedDonorByRecipient] = useState<
    Map<number, number>
  >(new Map());

  useEffect(() => {
    if (whId == null && warehouseId != null) setWhId(warehouseId);
  }, [warehouseId, whId]);

  // ─── Справочници ──────────────────────────────────────────────────────────
  const productById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);
  const decimalsOf = useCallback(
    (productId: number): number => clampDecimals(productById.get(productId)?.decimalPlaces),
    [productById],
  );
  // Наличностите се четат КЪМ ИЗБРАНАТА ДАТА → липси/излишъци са спрямо нея.
  const { data: stockBalances = [] } = useStockBalances(whId, date || null);
  const balanceByProduct = useMemo(
    () => new Map(stockBalances.map((b) => [b.productId, b])),
    [stockBalances],
  );
  // Последните доставни цени — източникът на ед. цената на излишъка; докато не
  // са заредени, липсата на цена не е доказана (виж `priceMissing`)
  const { data: lastPrices = [], isSuccess: lastPricesReady } = useLastPrices(whId);
  const lastPriceByProduct = useMemo(
    () => new Map(lastPrices.map((lp) => [lp.productId, lp])),
    [lastPrices],
  );

  // ─── Флаг „незапазени промени" + черновата (браузър + сървър; само нова) ─
  // Състоянието и зареждането се четат лениво — редовете идват от hook-а по-долу.
  const draft = useStocktakeDraft({
    mode,
    homePath: HOME_PATH,
    routerState,
    sheet: () => ({
      date,
      warehouseId: whId,
      warehouseName,
      reason,
      rows,
      appliedDonorByRecipient,
      countedRows,
      recipes: recipes.snapshot(),
    }),
    apply: (payload) => applyDraft(payload),
    onLoadError: () => onLoadError(),
  });
  const { dirtyRef, touch, resume } = draft;

  // ─── Приготвените рецепти + редовете (state + изчисления + блокове + суми) ─
  const { data: recipeList = [] } = useRecipes();
  const recipes = useStocktakeRecipes({ onTouch: touch, productById, decimalsOf });
  // Редовете: живите + изтритите с наличност, от документа или от рецептите (виж хука)
  const { countableProducts, rememberDocumentProducts } = useStocktakeCountableProducts(
    products, balanceByProduct, recipes.inRecipesByProduct,
  );
  const rowsActions = useStocktakeRowsActions({
    products: countableProducts,
    warehouseId: whId,
    balanceByProduct,
    lastPriceByProduct,
    savedSystemByProduct,
    vatRegistered,
    decimalsOf,
    onTouch: touch,
    interchangeGroups,
    appliedDonorByRecipient,
    hideZeroStock,
    revealedProductIds,
    pinnedProductIds,
    groupFilter,
    statusFilter,
    priceSourcesReady: lastPricesReady,
    inRecipesByProduct: recipes.inRecipesByProduct,
  });
  const { rows, replaceAllRows, countedRows, proposals, rowByProduct, view } = rowsActions;
  const substitutions = useStocktakeSubstitutions({
    proposals,
    appliedDonorByRecipient,
    setAppliedDonorByRecipient,
    onTouch: touch,
  });

  // ─── Търсачката и решетката (общата клавиатура на модула) ────────────────
  // Клетките са `stx-{продукт}-{колона}` — по ПРОДУКТ, не по индекс: блоковете
  // се пренареждат. Колони: 0 Намерено · 1 Ед. цена · 2 Стойност; последните
  // две ги има само при излишък → ←/→ ги прескачат, а ↑/↓ към ред без тях
  // отстъпва наляво до „Намерено". Enter слиза надолу (ред по ред). Първи са
  // редовете на приготвените рецепти (`r12`, полето „Порции" е в колона 0).
  const searchBox = useWorksheetSearch({
    getResultCount: () => searchRows.length,
    onPickIndex: (index) => picking.pickFromSearch(searchRows[index]),
    focusFirst: () => focusFirst(0),
  });
  // Двата списъка се сглобяват чак при натискане — не се копират на всеки рендер
  const rowKeySourcesRef = useRef<{ recipes: GridRowKey[]; products: GridRowKey[] }>({
    recipes: [],
    products: [],
  });
  const { focusCell, focusFirst, onGridKey, isInGrid } = useGridNavigation({
    prefix: "stx",
    colCount: COL_COUNT,
    getRowKeys: () => {
      const sources = rowKeySourcesRef.current;
      return sources.recipes.length ? [...sources.recipes, ...sources.products] : sources.products;
    },
    onEscape: searchBox.focusSearch,
    onLeaveTop: searchBox.focusSearch,
  });
  rowKeySourcesRef.current = { recipes: recipes.rowKeys, products: rowsActions.visibleProductIds };

  const warehouseName = warehouses.find((warehouse) => warehouse.id === whId)?.name ?? null;

  // ─── Напускане: нова — тихо (черновата се записва веднага); редакция — пита
  const requestLeave = useStocktakeLeave({
    mode,
    countedRows,
    recipeEntries: recipes.rows.map((row) => row.entry),
    dirtyRef,
    flushDraft: draft.sync.flush,
    onRegisterLeaveGuard,
  });
  const leaveToHome = () => requestLeave(() => navigate(HOME_PATH));

  // ─── Зареждане: чернова (нова) / редакция ────────────────────────────────
  const onLoadError = () => {
    message.error(t("inventoryPage.errLoad"));
    navigate(HOME_PATH);
  };
  const applyDraft = (payload: StocktakeDraft) => {
    setDate(payload.date || dayjs().format("YYYY-MM-DD"));
    setWhId(payload.warehouseId ?? warehouseId);
    setReason(payload.reason ?? "");
    rememberDocumentProducts(Object.keys(payload.rows).map(Number));
    replaceAllRows(payload.rows);
    // старите чернови нямат рецепти → празна секция
    recipes.apply(payload.recipes ?? null);
    setAppliedDonorByRecipient(
      new Map(payload.substitutions.map((s) => [s.recipientProductId, s.donorProductId])),
    );
  };
  useStocktakeLoad({
    mode,
    stocktakeId,
    actions: {
      applyEdit: (stocktake) => {
        const sheet = sheetOfStocktake(stocktake, decimalsOf);
        setDate(sheet.date);
        setWhId(sheet.warehouseId);
        setReason(sheet.reason);
        setSavedSystemByProduct(sheet.savedSystemByProduct);
        rememberDocumentProducts(sheet.productIds);
        replaceAllRows(sheet.rows);
        recipes.loadFromStocktake(stocktake);
        setAppliedDonorByRecipient(sheet.appliedDonorByRecipient);
      },
      onLoadError,
    },
  });

  // ─── Търсачка (продукти и рецепти) + избор от нея, от списъка и „Нов продукт"
  const { searchWords } = searchBox;
  const { searchRows } = useStocktakeSearchRows({
    products: countableProducts,
    recipes: recipeList,
    addedRecipeIds: recipes.addedRecipeIds,
    searchWords,
    rowByProduct,
    lastPriceByProduct,
    decimalsOf,
  });
  const picking = useStocktakePicking({
    setSearch: searchBox.setSearch,
    focusSearch: searchBox.focusSearch,
    focusCell,
    setRevealedProductIds,
    setPinnedProductIds,
    recipes: recipeList,
    addRecipes: recipes.addRecipes,
    counted: { rows, replaceAllRows, decimalsOf, touch },
  });

  // ─── „Занули склада" (само нова): въпросът с двата избора и прилагането ───
  const zeroing = useStocktakeZeroing({
    enabled: mode === "new", warehouseId: whId, warehouseName, date,
    rowsActions, balanceByProduct, reason, setReason, touch,
  });

  // ─── Запис (пречките и мутацията) ────────────────────────────────────────
  const { issues, submit, pending } = useStocktakeSave({
    mode,
    stocktakeId,
    homePath: HOME_PATH,
    warehouseId: whId,
    date,
    reason,
    countedRows,
    buildLines: rowsActions.buildLines,
    proposals,
    appliedDonorByRecipient,
    dirtyRef,
    recipes: {
      rows: recipes.rows,
      uncountedIngredients: rowsActions.uncountedIngredients,
      payload: recipes.payload,
    },
    onSaved: draft.onSaved,
    // Ctrl+Enter при пречка → клетката ѝ, дори редът да е скрит (рецепта → „Порции")
    onFocusIssue: (rowKey, col) => {
      if (typeof rowKey === "number") picking.reveal(rowKey);
      focusCell(rowKey, col);
    },
  });

  // ─── Глобални клавиши: Ctrl+Enter / Esc / ↓ / машина за писане ───────────
  useWorksheetKeyboard({
    isDesktop,
    searchInputRef: searchBox.inputRef,
    searchTextRef: searchBox.searchTextRef,
    setSearch: searchBox.setSearch,
    isInGrid,
    focusSearch: searchBox.focusSearch,
    focusFirst: () => focusFirst(0),
    onPrimary: submit,
    onLeave: leaveToHome,
    // черновата остава — изборът е с бутоните на промпта
    prompt: { isOpen: () => resume.pending != null, close: resume.dismiss },
  });

  const title =
    mode === "edit"
      ? `${t("inventoryPage.editStocktakeTitle")} №${stocktakeId}`
      : t("inventoryPage.newStocktake");
  // Взаимозамените: вдясно на десктоп, над продуктите на таблет, скрити на телефон
  const substitutionsPanel = proposals.length > 0 && !isNarrow && (
    <StocktakeSubstitutionsPanel
      proposals={proposals}
      appliedDonorByRecipient={appliedDonorByRecipient}
      selectedDonorByRecipient={substitutions.selectedDonorByRecipient}
      onSelectDonor={substitutions.selectDonor}
      onApply={substitutions.applySubstitution}
      onRevert={substitutions.revertSubstitution}
      layout={isDesktop ? "side" : "stacked"}
    />
  );

  return (
    <InventoryTheme controlHeight={40}>
      <div className="flex h-full flex-col overflow-hidden bg-canvas text-ink">
        <StocktakeWorksheetHeader
          band={{
            back: { label: t("inventoryPage.stocktake"), onClick: leaveToHome },
            title,
            right: mode === "new" ? <DraftPresence lockLostBy={draft.sync.lockLostBy} /> : null,
          }}
          date={date}
          onDateChange={(value) => {
            setDate(value);
            touch();
          }}
          warehouseId={whId}
          onWarehouseChange={(id) => {
            setWhId(id);
            // Друг склад = други наличности → преброеното става безсмислено
            replaceAllRows({});
            recipes.apply(null);
            setAppliedDonorByRecipient(new Map());
            setRevealedProductIds(new Set());
            touch();
          }}
          warehouses={warehouses}
          reason={reason}
          onReasonChange={(value) => {
            setReason(value);
            touch();
          }}
        />
        <div
          className={`flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto ${
            isNarrow ? "px-3 py-3" : "px-6 py-4"
          }`}
        >
          {/* Голяма търсачка „намери и скочи до реда" + „Целият списък" + „Нов продукт" */}
          <StocktakeSearchBar
            search={{
              attachInputRef: (node) => {
                searchBox.inputRef.current = node;
              },
              value: searchBox.search,
              onValueChange: searchBox.setSearch,
              onFocusChange: searchBox.setFocused,
              onInputKeyDown: searchBox.onInputKeyDown,
              dropdownOpen: searchBox.dropdownOpen,
              rows: searchRows,
              searchWords,
              highlightedIndex: searchBox.highlightedIndex,
              onHighlightChange: searchBox.setHighlightedIndex,
              onPick: picking.pickFromSearch,
            }}
            rows={rowsActions.allRows}
            recipes={recipeList}
            recipePortions={recipes.portionsByRecipe}
            onPickMany={picking.pickFromCatalog}
            onCatalogDismiss={searchBox.focusSearch}
            onSuppressDropdown={searchBox.suppressDropdown}
            onProductCreated={(product) => picking.onProductCreated(product.id)}
            onProductModalClosed={picking.onProductModalClosed}
            zeroAll={zeroing.button}
          />

          {!isDesktop && substitutionsPanel}

          {/* Продуктите + (по избор) секцията „Взаимозамени" вдясно */}
          <div className="flex min-h-0 flex-1 gap-3.5">
            <StocktakeProductsCard
              statusFilter={statusFilter}
              statusCounts={view.statusCounts}
              onStatusFilterChange={(filter) => {
                // Нов изглед → разкритите от търсачката се връщат под филтрите
                setStatusFilter(filter);
                setRevealedProductIds(new Set());
              }}
              groups={rowsActions.productGroups}
              groupFilter={groupFilter}
              onGroupFilterChange={(group) => {
                setGroupFilter(group);
                setRevealedProductIds(new Set());
              }}
              hideZeroStock={hideZeroStock}
              onToggleHideZeroStock={() => setHideZeroStock((on) => !on)}
              onGridKey={onGridKey}
              table={{
                blocks: view.blocks,
                recipes: recipes.section,
                substitutionProductIds: substitutions.substitutionProductIds,
                appliedProductIds: substitutions.appliedProductIds,
                isDesktop,
                onCellChange: rowsActions.onCellChange,
                onCellBlur: rowsActions.onCellBlur,
                onBatchChange: rowsActions.onBatchChange,
                onExpiryChange: rowsActions.onExpiryChange,
                totals: rowsActions.totals,
              }}
            />
            {/* Няма покриваеми липси → секцията изчезва и таблицата е 100% */}
            {isDesktop && substitutionsPanel}
          </div>
        </div>

        <StocktakeWorksheetFooter
          mode={mode}
          totalNet={rowsActions.totalNet}
          totalGross={rowsActions.totalGross}
          surplusNet={rowsActions.totals.surplusNet}
          shortageNet={rowsActions.totals.shortageNet}
          issue={issues[0]?.text ?? null}
          hasCounted={countedRows.length > 0 || recipes.rows.length > 0}
          saving={pending}
          draft={draft.footer()}
          onCancel={leaveToHome}
          onSave={submit}
        />

        <StocktakeDraftPrompt
          draft={resume.pending?.payload ?? null}
          productById={productById}
          decimalsOf={decimalsOf}
          onContinue={resume.continueDraft}
          onDiscard={() => {
            resume.discardDraft();
            recipes.apply(null); // листът започва празен — и без рецепти
          }}
          onDismiss={resume.dismiss}
        />
        {zeroing.dialog}
      </div>
    </InventoryTheme>
  );
};

export default StocktakeWorksheetPage;
